import * as fabric from 'fabric';
import QRCode from 'qrcode';
import { PosterConfig, PosterElement } from './poster-config';
import { urlToBase64 } from '../../../utils/common';

export class FabricPosterGenerator {
  private canvas: fabric.Canvas;
  private config: PosterConfig;
  private performanceStartTime: number;

  constructor(config: PosterConfig) {
    this.config = config;
    this.performanceStartTime = performance.now();

    // 创建fabric canvas
    const canvasElement = document.createElement('canvas');
    canvasElement.width = config.width;
    canvasElement.height = config.height;

    this.canvas = new fabric.Canvas(canvasElement, {
      width: config.width,
      height: config.height,
      backgroundColor: config.backgroundColor
    });
  }

  async generatePoster(): Promise<string> {
    console.log('🎨 开始Fabric.js海报生成...');

    try {
      // 按zIndex排序元素
      const sortedElements = this.config.elements.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

      // 逐个添加元素
      for (const element of sortedElements) {
        await this.addElement(element);
      }

      // 渲染canvas
      this.canvas.renderAll();

      // 生成base64
      const dataURL = this.canvas.toDataURL({
        format: 'jpeg',
        quality: 0.8,
        multiplier: 1
      });

      this.logPerformance();

      return dataURL;
    } catch (error) {
      console.error('❌ Fabric.js海报生成失败:', error);
      throw error;
    }
  }

  private async addElement(element: PosterElement): Promise<void> {
    switch (element.type) {
      case 'image':
        await this.addImage(element);
        break;
      case 'text':
        this.addText(element);
        break;
      case 'circle':
        this.addCircle(element);
        break;
      case 'qrcode':
        await this.addQRCode(element);
        break;
    }
  }

  private async addImage(element: PosterElement): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (!element.src) {
        resolve();
        return;
      }

      try {
        let imageSrc = element.src;
        if (element.src.startsWith('http')) {
          try {
            imageSrc = await urlToBase64(element.src);
          } catch (error) {
            console.warn(`图片转换base64失败，使用原始URL: ${element.id}`, error);
            imageSrc = element.src;
          }
        }

        fabric.FabricImage.fromURL(imageSrc, {
          crossOrigin: 'anonymous'
        }).then((img) => {
          if (!img) {
            console.warn(`图片加载失败: ${element.id}`);
            resolve();
            return;
          }

          // 只设置 scale，不设置 width/height
          img.set({
            left: element.left,
            top: element.top,
            scaleX: element.scaleX,
            scaleY: element.scaleY,
            selectable: false,
            evented: false,
            zIndex: element.zIndex,
            opacity: element.opacity
          });

          // 处理圆形裁剪
          if (element.clipPath === 'circle' && element.radius) {
            const clipPath = new fabric.Circle({
              radius: element.radius,
              left: element.left + element.radius,
              top: element.top + element.radius,
              originX: 'center',
              originY: 'center'
            });
            img.clipPath = clipPath;
          }

          this.canvas.add(img);
          resolve();
        }).catch((error) => {
          console.error(`图片加载失败: ${element.id}`, error);
          resolve();
        });
      } catch (error) {
        console.error(`图片处理失败: ${element.id}`, error);
        resolve();
      }
    });
  }

  private addText(element: PosterElement): void {
    if (!element.text) return;

    const textObj = new fabric.Text(element.text, {
      left: element.left,
      top: element.top,
      fontSize: element.fontSize || 14,
      fontFamily: element.fontFamily || 'Arial',
      fontWeight: element.fontWeight || 'normal',
      fill: element.fill || '#000000',
      selectable: false,
      evented: false
    });

    this.canvas.add(textObj);
  }

  private addCircle(element: PosterElement): void {
    if (!element.radius) return;

    const circle = new fabric.Circle({
      left: element.left - element.radius, // fabric.js的left是元素左边界
      top: element.top - element.radius,   // fabric.js的top是元素上边界
      radius: element.radius,
      fill: element.fill || '#CCCCCC',
      selectable: false,
      evented: false,
      originX: 'center',
      originY: 'center'
    });

    this.canvas.add(circle);
  }

  private async addQRCode(element: PosterElement): Promise<void> {
    if (!element.src) return;

    try {
      // 生成二维码
      const qrCodeDataURL = await QRCode.toDataURL(element.src, {
        width: element.width || 80,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      return fabric.FabricImage.fromURL(qrCodeDataURL, {
        crossOrigin: 'anonymous'
      }).then((img) => {
        if (img) {
          img.set({
            left: element.left,
            top: element.top,
            width: element.width,
            height: element.height,
            selectable: false,
            evented: false
          });
          this.canvas.add(img);
        }
      }).catch((error) => {
        console.error('二维码图片加载失败:', error);
      });
    } catch (error) {
      console.error('二维码生成失败:', error);
    }
  }

  private logPerformance(): void {
    const endTime = performance.now();
    const totalDuration = endTime - this.performanceStartTime;

    console.log('✅ Fabric.js海报生成完成');
    console.log('🎯 总耗时:', totalDuration.toFixed(2), 'ms');
    console.log('📊 元素数量:', this.config.elements.length);
    console.log('📏 画布尺寸:', `${this.config.width}x${this.config.height}`);
  }

  dispose(): void {
    this.canvas.dispose();
  }
}
